import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { MapPinIcon, CalendarIcon, ClockIcon, UserIcon, CheckCircle2Icon, ChevronRightIcon, ChevronLeftIcon } from 'lucide-react';
const locations = [{
  id: 'landover',
  name: 'Landover Clinic',
  address: '8700 Central Ave #302A, Landover, MD 20785'
}, {
  id: 'oxon',
  name: 'Oxon Hill Clinic',
  address: '6188 Oxon Hill Rd #601, Oxon Hill, MD 20745'
}];
const visitTypes = ['Primary Care', 'Urgent Care', 'DOT Physical', 'Pediatrics', "Women's Health", 'Follow-Up'];
const timeSlots = ['09:00 AM', '09:30 AM', '10:00 AM', '11:30 AM', '01:00 PM', '02:30 PM', '03:00 PM', '04:30 PM'];
export function BookAppointmentPage() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [bookingData, setBookingData] = useState({
    location: '',
    visitType: '',
    date: '',
    time: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    reason: ''
  });
  const updateData = (field: string, value: string) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleNext = () => setStep(s => Math.min(s + 1, 5));
  const handleBack = () => setStep(s => Math.max(s - 1, 1));
  const handleSubmit = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  if (isSuccess) {
    return <>
        <PageHero eyebrow="Booking Confirmed" title="See You Soon" subtitle="Your appointment has been successfully scheduled." data-id="element-278" />
        <section className="py-20 bg-gray-50 min-h-[50vh] flex items-center justify-center px-4" data-id="element-279">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-white p-8 md:p-10 rounded-[10px] shadow-lg max-w-lg w-full text-center border border-[#E5E9F0]" data-id="element-280">
            <CheckCircle2Icon className="h-20 w-20 text-teal-500 mx-auto mb-6" data-id="element-281" />
            <h2 className="text-3xl font-bold text-navy-500 mb-2" data-id="element-282">
              Appointment Confirmed
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-283">
              We've sent a confirmation email to {bookingData.email}.
            </p>

            <div className="bg-gray-50 rounded-[10px] p-6 text-left mb-8 border border-[#E5E9F0]" data-id="element-284">
              <p className="font-bold text-navy-500 mb-1" data-id="element-285">
                {bookingData.visitType}
              </p>
              <p className="text-sm text-gray-600 flex items-center gap-2 mb-1" data-id="element-286">
                <CalendarIcon className="h-4 w-4" data-id="element-287" /> {bookingData.date} at{' '}
                {bookingData.time}
              </p>
              <p className="text-sm text-gray-600 flex items-center gap-2" data-id="element-288">
                <MapPinIcon className="h-4 w-4" data-id="element-289" />{' '}
                {locations.find(l => l.id === bookingData.location)?.name}
              </p>
            </div>

            <button className="w-full bg-[#E8891D] hover:bg-[#d07a15] text-white py-3.5 rounded-full font-bold transition-colors mb-3" data-id="element-290">
              Add to Calendar
            </button>
            <a href="/" className="block w-full text-[#0A71B1] font-semibold hover:underline py-2" data-id="element-291">
              Return to Home
            </a>
          </motion.div>
        </section>
      </>;
  }
  return <>
      <PageHero eyebrow="Schedule a Visit" title="Book Your Appointment" subtitle="Select a location, choose your visit type, and find a time that works for you." data-id="element-292" />

      <section className="py-16 md:py-24 bg-gray-50" data-id="element-293">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-294">
          {/* Progress Bar */}
          <div className="mb-10" data-id="element-295">
            <div className="flex justify-between mb-2 px-1" data-id="element-296">
              {[1, 2, 3, 4, 5].map(i => <div key={i} className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= i ? 'bg-[#0A71B1] text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-297">
                  {i}
                </div>)}
            </div>
            <div className="relative h-2 bg-gray-200 rounded-full mx-4 -mt-5 -z-10" data-id="element-298">
              <motion.div className="absolute top-0 left-0 h-full bg-[#0A71B1] rounded-full" initial={{
              width: '0%'
            }} animate={{
              width: `${(step - 1) / 4 * 100}%`
            }} transition={{
              duration: 0.3
            }} data-id="element-299" />
            </div>
          </div>

          <motion.div className="bg-white rounded-[10px] shadow-sm border border-[#E5E9F0] p-6 md:p-10" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-300">
            {step === 1 && <div className="space-y-6" data-id="element-301">
                <h2 className="text-2xl font-bold text-navy-500 mb-6" data-id="element-302">
                  Select Location
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-303">
                  {locations.map(loc => <button key={loc.id} onClick={() => {
                updateData('location', loc.id);
                handleNext();
              }} className={`text-left p-6 rounded-[10px] border-2 transition-all ${bookingData.location === loc.id ? 'border-[#0A71B1] bg-blue-50' : 'border-[#E5E9F0] hover:border-[#0A71B1]/50 hover:bg-gray-50'}`} data-id="element-304">
                      <MapPinIcon className={`h-8 w-8 mb-4 ${bookingData.location === loc.id ? 'text-[#0A71B1]' : 'text-gray-400'}`} data-id="element-305" />
                      <h3 className="font-bold text-navy-500 text-lg mb-2" data-id="element-306">
                        {loc.name}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-307">{loc.address}</p>
                    </button>)}
                </div>
              </div>}

            {step === 2 && <div className="space-y-6" data-id="element-308">
                <h2 className="text-2xl font-bold text-navy-500 mb-6" data-id="element-309">
                  Reason for Visit
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-310">
                  {visitTypes.map(type => <button key={type} onClick={() => {
                updateData('visitType', type);
                handleNext();
              }} className={`p-4 rounded-[10px] border-2 font-semibold transition-all text-left ${bookingData.visitType === type ? 'border-[#0A71B1] bg-blue-50 text-[#0A71B1]' : 'border-[#E5E9F0] text-gray-700 hover:border-[#0A71B1]/50 hover:bg-gray-50'}`} data-id="element-311">
                      {type}
                    </button>)}
                </div>
              </div>}

            {step === 3 && <div className="space-y-8" data-id="element-312">
                <h2 className="text-2xl font-bold text-navy-500 mb-2" data-id="element-313">
                  Select Date & Time
                </h2>

                <div data-id="element-314">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-315">
                    Choose a Date
                  </label>
                  <input type="date" value={bookingData.date} onChange={e => updateData('date', e.target.value)} className="w-full sm:w-auto px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" min={new Date().toISOString().split('T')[0]} data-id="element-316" />
                </div>

                {bookingData.date && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} data-id="element-317">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-318">
                      Available Times
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-319">
                      {timeSlots.map(time => <button key={time} onClick={() => updateData('time', time)} className={`py-2 px-3 rounded-[10px] border text-sm font-semibold transition-all ${bookingData.time === time ? 'bg-[#0A71B1] border-[#0A71B1] text-white' : 'border-[#E5E9F0] text-gray-700 hover:border-[#0A71B1] hover:text-[#0A71B1]'}`} data-id="element-320">
                          {time}
                        </button>)}
                    </div>
                  </motion.div>}
              </div>}

            {step === 4 && <div className="space-y-6" data-id="element-321">
                <h2 className="text-2xl font-bold text-navy-500 mb-6" data-id="element-322">
                  Patient Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-323">
                  <div data-id="element-324">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-325">
                      First Name
                    </label>
                    <input type="text" value={bookingData.firstName} onChange={e => updateData('firstName', e.target.value)} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" data-id="element-326" />
                  </div>
                  <div data-id="element-327">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-328">
                      Last Name
                    </label>
                    <input type="text" value={bookingData.lastName} onChange={e => updateData('lastName', e.target.value)} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" data-id="element-329" />
                  </div>
                  <div data-id="element-330">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-331">
                      Phone Number
                    </label>
                    <input type="tel" value={bookingData.phone} onChange={e => updateData('phone', e.target.value)} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" data-id="element-332" />
                  </div>
                  <div data-id="element-333">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-334">
                      Email Address
                    </label>
                    <input type="email" value={bookingData.email} onChange={e => updateData('email', e.target.value)} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" data-id="element-335" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-336">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-337">
                      Additional Notes (Optional)
                    </label>
                    <textarea value={bookingData.reason} onChange={e => updateData('reason', e.target.value)} rows={3} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="Briefly describe your symptoms or reason for visit..." data-id="element-338" />
                  </div>
                </div>
              </div>}

            {step === 5 && <div className="space-y-6" data-id="element-339">
                <h2 className="text-2xl font-bold text-navy-500 mb-6" data-id="element-340">
                  Review & Confirm
                </h2>
                <div className="bg-gray-50 rounded-[10px] p-6 border border-[#E5E9F0] space-y-4" data-id="element-341">
                  <div className="flex items-start gap-3" data-id="element-342">
                    <MapPinIcon className="h-5 w-5 text-[#0A71B1] mt-0.5" data-id="element-343" />
                    <div data-id="element-344">
                      <p className="text-sm text-gray-500" data-id="element-345">Location</p>
                      <p className="font-semibold text-navy-500" data-id="element-346">
                        {locations.find(l => l.id === bookingData.location)?.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-347">
                    <UserIcon className="h-5 w-5 text-[#0A71B1] mt-0.5" data-id="element-348" />
                    <div data-id="element-349">
                      <p className="text-sm text-gray-500" data-id="element-350">Visit Type</p>
                      <p className="font-semibold text-navy-500" data-id="element-351">
                        {bookingData.visitType}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-352">
                    <CalendarIcon className="h-5 w-5 text-[#0A71B1] mt-0.5" data-id="element-353" />
                    <div data-id="element-354">
                      <p className="text-sm text-gray-500" data-id="element-355">Date & Time</p>
                      <p className="font-semibold text-navy-500" data-id="element-356">
                        {bookingData.date} at {bookingData.time}
                      </p>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-gray-200 mt-4" data-id="element-357">
                    <p className="text-sm text-gray-500" data-id="element-358">Patient Details</p>
                    <p className="font-semibold text-navy-500" data-id="element-359">
                      {bookingData.firstName} {bookingData.lastName}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-360">
                      {bookingData.phone} | {bookingData.email}
                    </p>
                  </div>
                </div>
              </div>}

            {/* Navigation Buttons */}
            <div className="mt-10 pt-6 border-t border-[#E5E9F0] flex items-center justify-between" data-id="element-361">
              {step > 1 ? <button onClick={handleBack} className="flex items-center gap-2 px-6 py-3 rounded-[10px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-362">
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-363" /> Back
                </button> : <div data-id="element-364"></div>}

              {step < 5 ? <button onClick={handleNext} disabled={step === 1 && !bookingData.location || step === 2 && !bookingData.visitType || step === 3 && (!bookingData.date || !bookingData.time) || step === 4 && (!bookingData.firstName || !bookingData.lastName || !bookingData.phone)} className="flex items-center gap-2 bg-[#0A71B1] hover:bg-[#085d91] text-white px-8 py-3 rounded-[10px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-365">
                  Next <ChevronRightIcon className="h-5 w-5" data-id="element-366" />
                </button> : <button onClick={handleSubmit} disabled={isSubmitting} className="flex items-center gap-2 bg-[#E8891D] hover:bg-[#d07a15] text-white px-8 py-3 rounded-full font-semibold transition-colors disabled:opacity-70" data-id="element-367">
                  {isSubmitting ? 'Confirming...' : 'Confirm Booking'}{' '}
                  <CheckCircle2Icon className="h-5 w-5" data-id="element-368" />
                </button>}
            </div>
          </motion.div>
        </div>
      </section>
    </>;
}